import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { VibesitesImage } from '@vibesites/ui-primitives';

const separatorImageDef: SlotGroupDefinition = {
  label: 'Separator Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

/**
 * Full-bleed band that visually divides the apartment and community
 * galleries: a coloured panel with a headline on the left and a featured
 * image on the right.
 *
 * Price Brothers override of the base rise theme separator: the image sits
 * beside the headline instead of being pulled under it, so the text never
 * overlaps the photo.
 */
export function GallerySeparator() {
  const img = useSlotGroup('galleryseparator.image', separatorImageDef);

  return (
    <section className="bg-white">
      <div className="gallery-separator">
        <div className="gallery-separator__inner">
          {/* Title lives on the left in the coloured band */}
          <div className="gallery-separator__title-box">
            <RichTextContent
              path="galleryseparator.title"
              as="h2"
              className="gallery-separator__title"
            />
          </div>

          {/* Image on the right, sitting alongside the text (no overlap) */}
          <div className="gallery-separator__image-outer">
            <EditableSlotGroup
              groupPath="galleryseparator.image"
              definition={separatorImageDef}
            >
              <div className="gallery-separator__image-wrap">
                <VibesitesImage
                  url={img.image || ''}
                  alt={img.alt || ''}
                  sizes="(min-width: 640px) 60vw, 100vw"
                  className="w-full h-full object-cover"
                />
              </div>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Separator — full viewport width */
        .gallery-separator {
          width: 100%;
          background-color: var(--vibe-color-primary);
          padding: 6.5rem 1.5rem;
        }

        .gallery-separator__inner {
          max-width: 72rem;
          margin-left: auto;
          margin-right: auto;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 2.5rem;
        }

        /* Left column — text sits on the coloured band */
        .gallery-separator__title-box {
          flex: 0 0 34%;
          position: relative;
          padding-right: 1.5rem;
        }

        .gallery-separator__title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 3vw, 2.75rem);
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          line-height: 1.15;
          color: var(--vibe-color-text-on-primary);
          margin: 0;
        }

        .gallery-separator__title-accent {
          color: #ffffff;
        }

        /* Right column — image sits beside the text */
        .gallery-separator__image-outer {
          flex: 1 1 0;
          position: relative;
        }

        .gallery-separator__image-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 7;
          overflow: hidden;
          border-radius: 3px;
        }

        @media (max-width: 639px) {
          .gallery-separator__inner {
            flex-direction: column;
            gap: 1.25rem;
          }
          .gallery-separator__title-box {
            flex: 0 0 auto;
            width: 100%;
            padding-right: 0;
          }
          .gallery-separator__title {
            text-align: center;
          }
          .gallery-separator__image-outer {
            width: 100%;
          }
          .gallery-separator__image-wrap {
            aspect-ratio: 4 / 3;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GallerySeparator',
  displayName: 'Gallery Separator',
  tokens: {
    'galleryseparator.title': {
      label: 'Headline',
      type: 'richtext',
      defaultValue:
        '<p>A Life <span class="gallery-separator__title-accent">Elevated</span> to the Highest Expectations</p>',
    },
  },
  slotGroups: {
    'galleryseparator.image': separatorImageDef,
  },
};

(GallerySeparator as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GallerySeparator;
